import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Sparkles, AlertTriangle, Info } from 'lucide-react';
import { api } from '../../lib/api';
import type { LegalDoc, LegalDocKey } from '../../lib/types';
import { RichTextEditor } from '../../components/ui/RichTextEditor';

type Locale = 'es' | 'en';

const DOC_META: Record<LegalDocKey, { name: string; hint: string }> = {
  privacy: {
    name: 'Aviso de Privacidad',
    hint: 'Obligatorio en México cuando recoges datos de tus visitantes (formularios, reservas, contacto).',
  },
  terms: {
    name: 'Términos y Condiciones',
    hint: 'Reglas de uso de tu sitio. Recomendado, y necesario si vendes productos o servicios en línea.',
  },
  cookies: {
    name: 'Aviso de Cookies',
    hint: 'Explica qué guarda tu sitio en el navegador de las visitas.',
  },
};

interface Draft { title: string; body: string }
type DraftMap = Record<LegalDocKey, Record<Locale, Draft>>;

function hasPlaceholders(body: string): boolean {
  return body.includes('[COMPLETAR');
}

export default function LegalTabContent({ onSaved }: { onSaved?: () => void } = {}) {
  const [docs, setDocs] = useState<LegalDoc[] | null>(null);
  const [drafts, setDrafts] = useState<DraftMap | null>(null);
  const [activeLocale, setActiveLocale] = useState<Record<LegalDocKey, Locale>>({ privacy: 'es', terms: 'es', cookies: 'es' });
  const [busy, setBusy] = useState<string | null>(null); // `${docKey}:${locale}:${action}`

  useEffect(() => { load(); }, []);

  async function load() {
    const data = await api.legal.get();
    setDocs(data);
    const next = {} as DraftMap;
    for (const doc of data) {
      next[doc.docKey] = {
        es: { title: doc.locales.es?.title ?? doc.defaultTitle.es, body: doc.locales.es?.body ?? '' },
        en: { title: doc.locales.en?.title ?? doc.defaultTitle.en, body: doc.locales.en?.body ?? '' },
      };
    }
    setDrafts(next);
  }

  function setDraft(docKey: LegalDocKey, locale: Locale, patch: Partial<Draft>) {
    setDrafts((prev) => prev && ({
      ...prev,
      [docKey]: { ...prev[docKey], [locale]: { ...prev[docKey][locale], ...patch } },
    }));
  }

  async function generate(docKey: LegalDocKey, locale: Locale) {
    const current = drafts?.[docKey][locale];
    if (current && current.body.trim() && current.body !== '<p></p>') {
      const ok = window.confirm('Esto reemplazará el texto actual con una nueva plantilla basada en los datos de tu negocio. ¿Continuar?');
      if (!ok) return;
    }
    setBusy(`${docKey}:${locale}:gen`);
    try {
      const { title, body } = await api.legal.fromTemplate(docKey, locale);
      setDraft(docKey, locale, { title, body });
    } finally {
      setBusy(null);
    }
  }

  async function save(docKey: LegalDocKey, locale: Locale) {
    const draft = drafts?.[docKey][locale];
    if (!draft) return;
    setBusy(`${docKey}:${locale}:save`);
    try {
      await api.legal.save(docKey, { locale, title: draft.title, body: draft.body, publish: true });
      await load();
      onSaved?.();
    } finally {
      setBusy(null);
    }
  }

  if (!docs || !drafts) {
    return <p className="text-sm text-ui-text-subtle py-8 text-center">Cargando…</p>;
  }

  return (
    <div className="space-y-5">
      {/* Descargo de responsabilidad */}
      <div className="flex gap-3 rounded-lg border border-amber-500/25 bg-amber-500/[0.06] px-3.5 py-3.5">
        <AlertTriangle size={15} className="text-amber-500 shrink-0 mt-0.5" />
        <div className="space-y-1 min-w-0">
          <p className="text-sm font-medium text-ui-text">Plantillas orientativas</p>
          <p className="text-sm text-ui-text-subtle leading-relaxed">
            Te damos un punto de partida con los datos de tu negocio ya incluidos. Revisa y completa
            las partes marcadas con <span className="font-mono text-ui-text-muted">[COMPLETAR]</span>.
            Tú eres responsable del contenido legal final de tu sitio.
          </p>
        </div>
      </div>

      <div className="flex gap-2 rounded-lg border border-ui-border bg-ui-surface px-3.5 py-3 items-center">
        <Info size={14} className="text-ui-text-subtle shrink-0" />
        <p className="text-sm text-ui-text-subtle">
          Después de guardar, recuerda usar <strong className="text-ui-text-muted">«Publicar sitio»</strong> para que tus visitantes vean los cambios.
        </p>
      </div>

      {docs.map((doc, idx) => {
        const meta = DOC_META[doc.docKey];
        const locale = activeLocale[doc.docKey];
        const draft = drafts[doc.docKey][locale];
        const serverLocale = doc.locales[locale];
        const published = serverLocale?.status === 'published';
        const showPlaceholderWarning = hasPlaceholders(draft.body);

        return (
          <motion.div
            key={doc.docKey}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: idx * 0.05 }}
            className="rounded-lg border border-ui-border bg-ui-surface overflow-hidden"
          >
            <div className="flex items-start justify-between gap-4 px-4 py-3.5 border-b border-ui-border">
              <div className="flex items-start gap-2.5 min-w-0">
                <ShieldCheck size={16} className="text-ui-accent shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-ui-text">{meta.name}</span>
                    {serverLocale ? (
                      <span className={`text-xs ${published ? 'text-ui-success' : 'text-ui-warning'}`}>
                        {published ? 'guardado' : 'borrador'}
                      </span>
                    ) : (
                      <span className="text-xs text-ui-text-subtle">sin crear</span>
                    )}
                  </div>
                  <p className="text-sm text-ui-text-subtle mt-0.5">{meta.hint}</p>
                </div>
              </div>

              {/* Locale toggle */}
              <div className="flex gap-1 p-1 bg-ui-bg rounded-lg border border-ui-border shrink-0">
                {(['es', 'en'] as Locale[]).map((loc) => (
                  <button
                    key={loc}
                    onClick={() => setActiveLocale((p) => ({ ...p, [doc.docKey]: loc }))}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                      locale === loc
                        ? 'bg-ui-surface-2 text-ui-text border border-ui-border-strong'
                        : 'text-ui-text-subtle hover:text-ui-text-muted'
                    }`}
                  >
                    {loc.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm text-ui-text-muted mb-1">Título</label>
                <input
                  value={draft.title}
                  onChange={(e) => setDraft(doc.docKey, locale, { title: e.target.value })}
                  className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent"
                />
              </div>

              <div>
                <label className="block text-sm text-ui-text-muted mb-1">Contenido</label>
                <RichTextEditor
                  key={`${doc.docKey}:${locale}`}
                  value={draft.body}
                  onChange={(body) => setDraft(doc.docKey, locale, { body })}
                />
              </div>

              {showPlaceholderWarning && (
                <div className="flex gap-2 text-sm text-ui-warning">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span>Aún hay partes por completar marcadas con <span className="font-mono">[COMPLETAR]</span>.</span>
                </div>
              )}

              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={() => save(doc.docKey, locale)}
                  disabled={busy === `${doc.docKey}:${locale}:save`}
                  className="px-4 py-2 bg-ui-accent hover:bg-ui-accent-hover text-ui-text-inverse text-sm font-medium rounded-md disabled:opacity-50 transition-colors"
                >
                  {busy === `${doc.docKey}:${locale}:save` ? 'Guardando…' : 'Guardar'}
                </button>
                <button
                  onClick={() => generate(doc.docKey, locale)}
                  disabled={busy === `${doc.docKey}:${locale}:gen`}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-ui-text-muted hover:text-ui-text border border-ui-border hover:border-ui-border-strong rounded-md disabled:opacity-50 transition-colors"
                >
                  <Sparkles size={14} />
                  {busy === `${doc.docKey}:${locale}:gen` ? 'Generando…' : serverLocale ? 'Reaplicar plantilla' : 'Generar desde plantilla'}
                </button>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
